import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { HardDrive, Loader2, Download, Link2, Unplug } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export const DRIVE = `${process.env.REACT_APP_BACKEND_URL}/api/drive`;
const ROWS = [["white_bg", "Poze pe fundal alb"], ["boxes", "Poze cutii produse"], ["export", "Folder pentru creative (trimitere)"]];

const ImportState = ({ s }) => !s ? null : (
  <span className="text-[11px] text-zinc-500" data-testid={`drive-import-state-${s.lib}`}>
    {s.status === "running" ? `se importă… ${s.imported + s.skipped + s.failed}/${s.found || "?"}` : s.status === "error" ? `eroare: ${s.error}` : `gata: ${s.imported} noi, ${s.skipped} existente${s.failed ? `, ${s.failed} eșuate` : ""}`}
  </span>
);

export const DriveDialog = ({ onClose, onImported }) => {
  const [d, setD] = useState(null);
  const [links, setLinks] = useState({});
  const [busy, setBusy] = useState("");
  const load = useCallback(() => axios.get(`${DRIVE}/status`).then((r) => {
    setD(r.data); setLinks((l) => Object.keys(l).length ? l : Object.fromEntries(ROWS.map(([k]) => [k, r.data.folders?.[k]?.link || ""])));
    return r.data;
  }), []);
  useEffect(() => { load(); }, [load]);
  const running = d && Object.values(d.import || {}).some((s) => s.status === "running");
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => load().then((x) => { if (!Object.values(x.import || {}).some((s) => s.status === "running")) onImported(); }), 4000);
    return () => clearInterval(t);
  }, [running, load, onImported]);
  const act = async (k, fn) => { setBusy(k); try { await fn(); await load(); } catch (e) { toast.error(e.response?.data?.detail || e.message); } setBusy(""); };
  const connect = () => act("c", async () => { const r = await axios.get(`${DRIVE}/connect`); window.location.href = r.data.url; });
  const save = () => act("s", async () => { await axios.put(`${DRIVE}/folders`, links); toast.success("Foldere salvate"); });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="drive-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><HardDrive className="h-4 w-4" /> Google Drive</DialogTitle></DialogHeader>
        {!d ? <Loader2 className="h-4 w-4 animate-spin" /> : !d.connected ? (
          <div className="space-y-3 text-sm">
            <p className="text-zinc-500">Conectează contul Google în care ai folderele cu poze. Aplicația citește pozele și poate salva creativele în folderul ales.</p>
            <button data-testid="drive-connect-btn" onClick={connect} disabled={!!busy} className="rounded-full bg-amber-400 px-4 py-2 text-sm font-semibold text-zinc-950">Conectează Google Drive</button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs text-zinc-500" data-testid="drive-connected">Conectat: <b>{d.email}</b>
              <button data-testid="drive-disconnect-btn" onClick={() => window.confirm("Deconectez Google Drive?") && act("x", () => axios.delete(DRIVE))} className="ml-auto inline-flex items-center gap-1 hover:text-rose-500"><Unplug className="h-3 w-3" /> Deconectează</button>
            </div>
            {ROWS.map(([k, label]) => (
              <div key={k} className="space-y-1">
                <div className="flex items-center gap-2 text-xs"><span className="font-medium">{label}</span>
                  {d.folders?.[k]?.name && <span className="text-zinc-500" data-testid={`drive-folder-name-${k}`}>· {d.folders[k].name}</span>}
                  {k !== "export" && <span className="text-zinc-500">· {d.counts?.[k] || 0} poze în aplicație</span>}</div>
                <div className="flex gap-2">
                  <Input data-testid={`drive-link-${k}`} value={links[k] || ""} onChange={(e) => setLinks({ ...links, [k]: e.target.value })} placeholder="https://drive.google.com/drive/folders/…" />
                  {k !== "export" && <button data-testid={`drive-import-${k}`} disabled={!!busy || !d.folders?.[k]} onClick={() => act(k, () => axios.post(`${DRIVE}/import/${k}`))} className="inline-flex shrink-0 items-center gap-1 rounded-full border px-3 text-xs disabled:opacity-40">
                    {busy === k ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Importă</button>}
                </div>
                <ImportState s={d.import?.[k]} />
              </div>
            ))}
            <button data-testid="drive-save-folders" onClick={save} disabled={!!busy} className="inline-flex items-center gap-1.5 rounded-full bg-amber-400 px-4 py-1.5 text-sm font-semibold text-zinc-950 disabled:opacity-50">
              {busy === "s" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Salvează folderele</button>
            <p className="text-[11px] text-zinc-500">Importul ia și subfolderele (numele subfolderului devine eticheta, ex. numele produsului). Pozele deja importate nu se dublează — poți apăsa „Importă” oricând ca să aduci doar pozele noi.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
